/**
 * Permanent right-side leaderboard for Anagram round/reveal scenes, with an
 * auto-cycling view toggle.
 *
 * Two views, ~12s apart, cross-fade:
 *   - "ALL TIME · TOP 50" — top 50 across all sessions for the tenant.
 *   - "THIS SESSION"      — current game session standings.
 *
 * Layout: 380px wide, absolutely positioned at left=1500..1880 (matches the
 * Who Am I + Survey Says reworks for visual parity across games).
 *
 * @packageDocumentation
 */

import type { LbRow } from '@sg/shared';
import { ensureLobbyLbStyles } from './lobby-leaderboard.js';

export interface SideLbRowInput {
  rank: number;
  player_id: number;
  display_name: string;
  total_points: number;
}

export interface SideLeaderboardHandle {
  el: HTMLDivElement;
  /** Set the all-time top-50 dataset (used by view A). */
  setAllTimeRows: (rows: LbRow[] | SideLbRowInput[]) => void;
  /** Set the session standings (used by view B). */
  setSessionRows: (rows: SideLbRowInput[]) => void;
  setVisible: (v: boolean) => void;
  dispose: () => void;
}

/** Auto-cycle interval (ms) between the two views. */
const VIEW_INTERVAL_MS = 12_000;
/** Cross-fade duration (ms). */
const FADE_MS = 360;

const VARIANT_STYLES = `
  @keyframes an-sidelb-in {
    from { opacity: 0; transform: translateX(12px); }
    to   { opacity: 1; transform: translateX(0);    }
  }
  .an-lblb[data-role="game-side-lb"] {
    position: absolute;
    top: 120px;
    left: 1500px;
    width: 380px;
    height: 840px;
    z-index: 50;
    pointer-events: none;
    animation: an-sidelb-in 280ms ease-out;
    transition: opacity ${FADE_MS}ms ease;
  }
  .an-lblb[data-role="game-side-lb"].fading-out { opacity: 0; }
  .an-lblb[data-role="game-side-lb"].hidden { display: none; }
  .an-lblb[data-role="game-side-lb"] .an-lblb-title {
    font-size: 1.25rem;
    letter-spacing: 0.22em;
    padding: 10px 8px 8px 8px;
  }
  .an-lblb[data-role="game-side-lb"] .an-lblb-head,
  .an-lblb[data-role="game-side-lb"] .an-lblb-row {
    grid-template-columns: 40px 1fr 86px;
  }
  .an-lblb[data-role="game-side-lb"] .an-lblb-head {
    font-size: 0.72rem;
    padding: 6px 12px 4px 12px;
  }
  .an-lblb[data-role="game-side-lb"] .an-lblb-row {
    padding: 8px 12px;
    font-size: 1.0rem;
  }
  .an-lblb[data-role="game-side-lb"] .an-lblb-rank { font-size: 1.1rem; }
  .an-lblb[data-role="game-side-lb"] .an-lblb-points { font-size: 1.05rem; }
  .an-lblb[data-role="game-side-lb"] .an-lblb-empty {
    padding: 20px 14px;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

function ensureVariantStyles(): void {
  if (document.getElementById('an-sidelb-variant-style')) return;
  const s = document.createElement('style');
  s.id = 'an-sidelb-variant-style';
  s.textContent = VARIANT_STYLES;
  document.head.appendChild(s);
}

function medalFor(rank: number): string {
  if (rank === 1) return 'an-r1';
  if (rank === 2) return 'an-r2';
  if (rank === 3) return 'an-r3';
  return '';
}

type View = 'all_time' | 'session';

export function createSideLeaderboard(mount: HTMLElement): SideLeaderboardHandle {
  ensureLobbyLbStyles();
  ensureVariantStyles();

  const el = document.createElement('div');
  el.className = 'an-lblb';
  el.setAttribute('data-role', 'game-side-lb');
  el.innerHTML = `
    <div class="an-lblb-title" data-role="title" data-testid="an-sidelb-title">ALL TIME &middot; TOP 50</div>
    <div class="an-lblb-head"><div>#</div><div>PLAYER</div><div>POINTS</div></div>
    <div class="an-lblb-body">
      <div class="an-lblb-list" data-role="rows">
        <div class="an-lblb-empty">No scores yet &mdash; be the first to answer!</div>
      </div>
    </div>
  `;
  mount.appendChild(el);
  const titleEl = el.querySelector<HTMLDivElement>('[data-role="title"]')!;
  const rowsContainer = el.querySelector<HTMLDivElement>('[data-role="rows"]')!;

  let allTimeRows: SideLbRowInput[] = [];
  let sessionRows: SideLbRowInput[] = [];
  let view: View = 'all_time';
  let cycleTimer: ReturnType<typeof setInterval> | null = null;

  function renderRows(rows: SideLbRowInput[]): void {
    if (rows.length === 0) {
      rowsContainer.innerHTML =
        '<div class="an-lblb-empty">No scores yet &mdash; be the first to answer!</div>';
      return;
    }
    const fragment = document.createDocumentFragment();
    for (const r of rows.slice(0, 10)) {
      const row = document.createElement('div');
      row.className = `an-lblb-row ${medalFor(r.rank)}`.trim();
      row.innerHTML = `
        <div class="an-lblb-rank">${r.rank}</div>
        <div class="an-lblb-name"></div>
        <div class="an-lblb-points">${r.total_points.toLocaleString()}</div>
      `;
      const nameEl = row.querySelector<HTMLDivElement>('.an-lblb-name')!;
      nameEl.textContent = r.display_name;
      fragment.appendChild(row);
    }
    rowsContainer.replaceChildren(fragment);
  }

  function applyView(): void {
    const rows = view === 'all_time' ? allTimeRows : sessionRows;
    titleEl.innerHTML = view === 'all_time'
      ? 'ALL TIME &middot; TOP 50'
      : 'THIS SESSION';
    titleEl.setAttribute('data-view', view);
    renderRows(rows);
  }

  function fadeAndSwap(): void {
    el.classList.add('fading-out');
    setTimeout(() => {
      view = view === 'all_time' ? 'session' : 'all_time';
      applyView();
      el.classList.remove('fading-out');
    }, FADE_MS);
  }

  function startCycle(): void {
    if (cycleTimer !== null) return;
    cycleTimer = setInterval(fadeAndSwap, VIEW_INTERVAL_MS);
  }
  function stopCycle(): void {
    if (cycleTimer !== null) {
      clearInterval(cycleTimer);
      cycleTimer = null;
    }
  }

  function setAllTimeRows(rows: LbRow[] | SideLbRowInput[]): void {
    allTimeRows = (rows as Array<LbRow | SideLbRowInput>).map((r) => ({
      rank: r.rank,
      player_id: r.player_id,
      display_name: r.display_name,
      total_points: (r as { total_points: number }).total_points,
    }));
    if (view === 'all_time') applyView();
  }

  function setSessionRows(rows: SideLbRowInput[]): void {
    sessionRows = rows.slice();
    if (view === 'session') applyView();
  }

  function setVisible(v: boolean): void {
    el.classList.toggle('hidden', !v);
    if (v) startCycle();
    else stopCycle();
  }

  // Start with the all-time view rendered (empty state until data lands).
  applyView();
  startCycle();

  return {
    el,
    setAllTimeRows,
    setSessionRows,
    setVisible,
    dispose() {
      stopCycle();
      el.remove();
    },
  };
}
